/**
 * TypeScript-speiling av kontraktene i forvaltning-api (Forvaltning.Sync).
 *
 * API-et bruker .NET minimal-API med System.Text.Json: egenskapsnavn
 * serialiseres som camelCase, og enums serialiseres som heltall.
 */

/** Retning for synkronisering. Heltall slik API-et forventer/returnerer. */
export enum SyncDirection {
    AgolToCentral = 1,
    CentralToAgol = 2,
}

/** Hva som utløste en synkronisering. */
export enum TriggerSource {
    Webhook = 1,
    Manual = 2,
    Scheduler = 3,
}

/** Type tidsplan for tidsstyrt synk. Heltall slik API-et forventer/returnerer. */
export enum ScheduleKind {
    Interval = 1,
    Cron = 2,
}

/**
 * En tidsplan styrer at en gitt (kobling, retning) periodisk legges i synk-køen
 * (TriggerSource.Scheduler). Enten et enkelt intervall eller et cron-uttrykk.
 * GET /admin/bindings/{bindingId}/schedules.
 */
export interface SyncSchedule {
    bindingId: string;
    direction: SyncDirection;
    enabled: boolean;
    kind: ScheduleKind;
    intervalSeconds?: number | null;
    cronExpression?: string | null;
    timeZoneId?: string | null;
    /** Når den planlagte kjøringen sist ble lagt i køen (null før første kjøring). */
    lastRunUtc?: string | null;
    /** Når neste planlagte kjøring forfaller. */
    nextDueUtc?: string | null;
    createdUtc: string;
    updatedUtc: string;
}

/**
 * Request-body for å opprette/oppdatere en tidsplan
 * (PUT /admin/bindings/{bindingId}/schedules/{direction}).
 */
export interface ScheduleUpsertRequest {
    kind: ScheduleKind;
    /** Påkrevd når kind = Interval. Minst 60 sekunder. */
    intervalSeconds?: number | null;
    /** Påkrevd når kind = Cron. */
    cronExpression?: string | null;
    /** IANA-id (f.eks. 'Europe/Oslo') for cron. Tom → UTC. */
    timeZoneId?: string | null;
    enabled: boolean;
}

/** API-et krever minst 60 sekunder mellom planlagte kjøringer (ScheduleEvaluator.MinIntervalSeconds). */
export const MIN_INTERVAL_SECONDS = 60;

/** Hva som skjer med en verdi som ikke finnes i feltets verdimap. Heltall jf. API. */
export enum UnmappedValuePolicy {
    /** Send verdien videre uendret (default for fritekstfelt). */
    PassThrough = 0,
    /** Utelat attributtet fra payloaden. */
    Drop = 1,
    /** Feil endringen (dead-letter) — anbefalt for felt med kodeliste. */
    Fail = 2,
}

/** NGIS-sti som hvert lag MÅ mappe for å korrelere mot NGIS. */
export const LOKAL_ID_SOURCE_PATH = 'identifikasjon.lokalId';

/** Ett kode-par i en verdimap (AGOL ↔ NGIS). 1:1-par auto-inverteres av API-et. */
export interface ValueMapping {
    agolValue: string;
    ngisValue: string;
}

/** Mapping for ett felt: NGIS-sti → AGOL-feltnavn, med type-info og valgfri verdimap. */
export interface FieldMapping {
    ngisSourcePath: string;
    agolFieldName: string;
    dataType: ForvaltningsFieldDataType;
    isArray?: boolean;
    isDateOnly?: boolean;
    valueMap?: ValueMapping[] | null;
    onUnmapped?: UnmappedValuePolicy;
}

/** Mapping for ett AGOL-lag mot en NGIS-objekttype/geometri. */
export interface LayerMapping {
    agolLayerId: number;
    featureType: string;
    geometryKind: string;
    agolObjectIdField: string;
    agolGlobalIdField: string;
    fields: FieldMapping[];
}

/** Felt-mapping-profil for en AGOL-tjeneste. Lagres som JSON-blob på bindingen. */
export interface FieldMappingProfile {
    bindingId: string;
    layers: LayerMapping[];
}

/**
 * En synkroniseringskobling mellom et AGOL-feature-service og et sentralt datasett.
 *
 * Det finnes ÉN bindingstype. Hvordan AGOL-tjenesten ble til — provisjonert av oss eller
 * en eksisterende tjeneste vi kobler oss på — er et historisk faktum, ikke en variant i
 * modellen: alle bindinger har en felt-mapping-profil og en companion-tabell for
 * sync-metadata.
 */
export interface SyncBinding {
    bindingId: string;
    displayName: string;
    forvaltningssystem: string;
    centralDatasetId: string;
    agolBaseUrl: string;
    lokalIdField: string;
    enabled: boolean;
    createdUtc: string;
    updatedUtc: string;
    /** Serialisert {@link FieldMappingProfile}. Uten den kan bindingen ikke synkes. */
    mappingProfileJson?: string | null;
    /** Companion-tabellen som holder sync-metadata. Settes av serveren ved opprettelse. */
    companionTableUrl?: string | null;
    /** Editor-tracking-feltet tjenesten bruker (ekko-filteret AGOL → NGIS). */
    agolEditorField?: string | null;
    /** Edit-date-feltet tjenesten bruker (konflikt-vannmerket NGIS → AGOL). */
    agolEditDateField?: string | null;
    /** Retningssperre. Mangler på svar fra eldre API → tolkes som {@link SyncDirectionPolicy.Both}. */
    directionPolicy?: SyncDirectionPolicy | null;
}

/**
 * Hvilke retninger en kobling tillater. Påkrevd ved opprettelse: API-et avviser POST uten.
 * Ingen 0-verdi, slik at en manglende verdi aldri tolkes som et gyldig valg.
 */
export enum SyncDirectionPolicy {
    AgolToCentralOnly = 1,
    CentralToAgolOnly = 2,
    Both = 3,
}

export const DIRECTION_POLICY_LABEL: Record<SyncDirectionPolicy, string> = {
    [SyncDirectionPolicy.AgolToCentralOnly]: 'Kun AGOL → Sentral',
    [SyncDirectionPolicy.CentralToAgolOnly]: 'Kun Sentral → AGOL',
    [SyncDirectionPolicy.Both]: 'Begge veier',
};

export const DIRECTION_POLICY_HELP: Record<SyncDirectionPolicy, string> = {
    [SyncDirectionPolicy.AgolToCentralOnly]:
        'AGOL er originalen. Endringer sendes til sentralt datasett, men aldri tilbake til AGOL. ' +
        'Velg denne når det sentrale datasettet dekker et større område (f.eks. fylke) enn AGOL-tjenesten.',
    [SyncDirectionPolicy.CentralToAgolOnly]:
        'Sentralt datasett er kilden. AGOL-tjenesten er en kopi, og endringer i AGOL sendes ikke videre.',
    [SyncDirectionPolicy.Both]:
        'Toveis forvaltning. Endringer synkes begge veier, med konfliktdeteksjon.',
};

/** True hvis policyen tillater retningen. Manglende policy (eldre API) = begge veier. */
export function policyAllows(
    policy: SyncDirectionPolicy | null | undefined,
    direction: SyncDirection,
): boolean {
    switch (policy ?? SyncDirectionPolicy.Both) {
        case SyncDirectionPolicy.AgolToCentralOnly:
            return direction === SyncDirection.AgolToCentral;
        case SyncDirectionPolicy.CentralToAgolOnly:
            return direction === SyncDirection.CentralToAgol;
        case SyncDirectionPolicy.Both:
            return true;
        default:
            return false;
    }
}

/**
 * Request-body for å opprette/oppdatere en kobling (POST/PUT /admin/bindings).
 *
 * `mappingProfile` er valgfri i API-et: utelates den, kjører serveren preflight og genererer
 * profilen selv. Synkadmin sender den alltid eksplisitt, slik at det brukeren så og korrigerte
 * i veiviseren er nøyaktig det som lagres. Editor-feltene fastsettes av API-et fra
 * preflight-inspeksjonen når de utelates.
 */
export interface BindingUpsertRequest {
    bindingId: string;
    displayName: string;
    forvaltningssystem: string;
    centralDatasetId: string;
    agolBaseUrl: string;
    lokalIdField?: string;
    enabled: boolean;
    /** Hvert lag må mappe {@link LOKAL_ID_SOURCE_PATH}, ellers avvises requesten. */
    mappingProfile?: FieldMappingProfile | null;
    /** Overstyrer editor-tracking-feltet preflight fant. */
    agolEditorField?: string | null;
    /** Overstyrer edit-date-feltet preflight fant. */
    agolEditDateField?: string | null;
    /** Påkrevd ved opprettelse (POST). Utelatt ved PUT → API-et beholder eksisterende verdi. */
    directionPolicy?: SyncDirectionPolicy | null;
}

/** Forespørsel om å inspisere en AGOL-tjeneste før en binding settes opp. */
export interface PreflightRequest {
    forvaltningssystem: string;
    centralDatasetId: string;
    agolBaseUrl: string;
}

/** Forslag til verdimap-par fra preflight, med visningsetikett fra NGIS-domenet. */
export interface ValueMappingSuggestion {
    agolValue: string;
    ngisValue: string;
    label: string;
}

/** Én kode i en kodeliste (NGIS-domene eller AGOL coded-value-domene). */
export interface CodedValueInfo {
    code: string;
    name: string;
}

/** Ett tilgjengelig AGOL-felt (for nedtrekksvalg i mapping-UI-et). */
export interface AgolFieldInfo {
    name: string;
    alias: string;
    /** ESRI-felttype, f.eks. "esriFieldTypeString". */
    type: string;
    /** Feltets coded-value-domene i AGOL, hvis det har et. */
    codedValues?: CodedValueInfo[] | null;
}

/** Ett inspisert AGOL-lag, for overstyring av objekttype ↔ lag og felt-nedtrekk. */
export interface AgolLayerInfo {
    id: number;
    name: string;
    /** ESRI-geometritype, f.eks. "esriGeometryPolygon". */
    geometryType: string;
    objectIdField: string;
    globalIdField: string;
    fields: AgolFieldInfo[];
}

/** Forslag til mapping av ett NGIS-felt mot et AGOL-felt. */
export interface FieldMappingSuggestion {
    ngisSourcePath: string;
    agolFieldName?: string | null;
    /** "Exact" | "Normalized" | "Alias" | "None". */
    matchKind: string;
    dataType: ForvaltningsFieldDataType;
    isDateOnly: boolean;
    typeCompatible: boolean;
    valueMap?: ValueMappingSuggestion[] | null;
    unmatchedCodes?: string[] | null;
    /** NGIS-feltets kodeliste — leveres også når feltet ikke ble matchet mot AGOL. */
    ngisCodedValues?: CodedValueInfo[] | null;
}

/** Preflight-rapport for ett AGOL-lag matchet mot en NGIS-objekttype. */
export interface PreflightLayerReport {
    agolLayerId: number;
    agolLayerName: string;
    featureType: string;
    geometryKind: string;
    agolObjectIdField: string;
    agolGlobalIdField: string;
    editorField?: string | null;
    /** True hvis identifikasjon.lokalId har et AGOL-treff. */
    lokalIdMapped: boolean;
    fields: FieldMappingSuggestion[];
    unmappedNgisFields: string[];
    /** Alle felt på det matchede AGOL-laget — nedtrekksvalg for felt-mapping. */
    availableAgolFields?: AgolFieldInfo[] | null;
    /**
     * Edit-date-feltet tjenesten faktisk bruker. Konflikt-vannmerket NGIS → AGOL sammenligner
     * dette mot lagret `agolEditMoment` i companion-tabellen; mangler det, er bindingen ikke
     * trygt synkbar og preflight melder det i {@link PreflightReport.requiredActions}.
     */
    editDateField?: string | null;
}

/** Rapport fra POST /admin/bindings/preflight. */
export interface PreflightReport {
    changeTrackingEnabled: boolean;
    compatible: boolean;
    /**
     * "Ready" når tjenesten kan synkes som den er, ellers "ReviewMapping". Bare et hint —
     * {@link requiredActions} er det som faktisk sier hva som må rettes.
     */
    recommendedStrategy: string;
    layers: PreflightLayerReport[];
    requiredActions: string[];
    /**
     * Alle inspiserte AGOL-lag i tjenesten — lar brukeren overstyre objekttype ↔
     * lag og gir nedtrekksvalg for AGOL-felt. Krever API-utvidelse; når fraværende
     * faller UI-et tilbake til auto-matchet lag (lesbar) + fritekst for feltnavn.
     */
    availableAgolLayers?: AgolLayerInfo[] | null;
}

/** Request-body for manuell synkronisering (POST /sync/request). */
export interface ManualSyncRequest {
    direction: SyncDirection;
    source: TriggerSource;
    reason: string;
    /** Når null/utelatt → fan-out til alle aktiverte koblinger. */
    bindingId?: string | null;
}

export interface ManualSyncResponse {
    bindingId?: string | null;
    direction: SyncDirection;
    fanOutCount?: number;
}

/** Request-body for å provisjonere et AGOL feature-service (POST /agol/feature-services). */
export interface CreateFeatureServiceRequest {
    forvaltningssystem: string;
    datasetId: string;
    serviceName: string;
    overwriteExisting?: boolean;
}

export interface CreateFeatureServiceResult {
    serviceName: string;
    serviceUrl: string;
    itemId?: string | null;
    layerNames: string[];
    fieldCount: number;
}

/** Datasett-oppsummering fra katalogen (GET /datasets). */
export interface DatasetSummary {
    datasetId: string;
    name: string;
    description?: string | null;
    geometryType?: string | null;
    crs?: string | null;
}

export type ForvaltningsFieldDataType = 1 | 2 | 3 | 4 | 5 | 6; // String|Integer|Double|Boolean|Date|Json

export interface ForvaltningsCodedValue {
    code: string | number;
    name: string;
}

export interface ForvaltningsFieldDomain {
    type: 1 | 2; // CodedValue | Range
    codedValues?: ForvaltningsCodedValue[] | null;
    minValue?: number | null;
    maxValue?: number | null;
}

export interface ForvaltningsFieldDefinition {
    name: string;
    alias: string;
    dataType: ForvaltningsFieldDataType;
    nullable: boolean;
    length?: number | null;
    domain?: ForvaltningsFieldDomain | null;
    sourcePath: string;
}

export interface ForvaltningsExtent {
    xmin: number;
    ymin: number;
    xmax: number;
    ymax: number;
}

export interface ForvaltningsDatasetSchema {
    datasetId: string;
    name: string;
    description?: string | null;
    spatialReferenceWkid?: number | null;
    geometryType: string;
    fields: ForvaltningsFieldDefinition[];
    extent?: ForvaltningsExtent | null;
    layers?: unknown[] | null;
    navnerom?: string | null;
}

/** Et feature oversatt til målformat (esri/geojson). */
export interface TranslatedFeature {
    attributes: Record<string, unknown>;
    /** ESRI-geometri-JSON når format = "esri". */
    geometry: unknown;
}

export interface TranslatedFeatureResultMetadata {
    format: string;
    geometryType: string;
    spatialReferenceWkid?: number | null;
    fieldCount: number;
    featureCount: number;
    nextCursor?: string | null;
}

export interface TranslatedFeatureResult {
    features: TranslatedFeature[];
    metadata: TranslatedFeatureResultMetadata;
}

/** Kø-metrikker, parset fra prometheus-tekst på GET /metrics. */
export interface SyncRequestStats {
    pending: number;
    processing: number;
    deadLettered: number;
}

export interface HealthStatus {
    healthz: boolean;
    readyz: boolean;
}

export const FIELD_DATA_TYPE_LABEL: Record<ForvaltningsFieldDataType, string> = {
    1: 'Tekst',
    2: 'Heltall',
    3: 'Desimaltall',
    4: 'Boolsk',
    5: 'Dato',
    6: 'JSON',
};

export const DIRECTION_LABEL: Record<SyncDirection, string> = {
    [SyncDirection.AgolToCentral]: 'AGOL → Sentral',
    [SyncDirection.CentralToAgol]: 'Sentral → AGOL',
};

/**
 * Retningens enum-navn slik API-et forventer det i URL-stien for tidsplaner
 * (Enum.TryParse på `{direction}`). Responsen serialiserer derimot `direction`
 * som heltall, jf. {@link SyncDirection}.
 */
export const DIRECTION_PATH: Record<SyncDirection, string> = {
    [SyncDirection.AgolToCentral]: 'AgolToCentral',
    [SyncDirection.CentralToAgol]: 'CentralToAgol',
};

export const SCHEDULE_KIND_LABEL: Record<ScheduleKind, string> = {
    [ScheduleKind.Interval]: 'Intervall',
    [ScheduleKind.Cron]: 'Cron',
};

export const UNMAPPED_POLICY_LABEL: Record<UnmappedValuePolicy, string> = {
    [UnmappedValuePolicy.PassThrough]: 'Send uendret',
    [UnmappedValuePolicy.Drop]: 'Utelat felt',
    [UnmappedValuePolicy.Fail]: 'Feil endringen',
};

/** Kort etikett for preflight-treff-typen (FieldMappingSuggestion.matchKind). */
export const MATCH_KIND_LABEL: Record<string, string> = {
    Exact: 'Eksakt',
    Normalized: 'Normalisert',
    Alias: 'Alias',
    None: 'Ingen treff',
};
